import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FormWorkgroupInvitation } from '../../form/compositions/FormWorkgroupInvitation';
import { FramedContent } from '../../framed-content';
import { deriveInvitationText } from '../../../model/communication/Participant';

interface IWorkgroupInvitationForm {
  props: any;
  flowData: any;
  subtitle: string;
  saveAsDefaultLabel?: string;
}

const WorkgroupInvitationForm: React.FC<IWorkgroupInvitationForm> = ({ props, flowData, subtitle, saveAsDefaultLabel }) => {
  if (!flowData || !flowData?.itemData?.invitation) {
    props.setNextStepDisabled(true);
  } else {
    props.setNextStepDisabled(false);
  }
  return (
    <FramedContent>
      <FormWorkgroupInvitation
        formData={flowData}
        headline={'Ok, noch ein paar Worte zu Ihrer Einladung?'}
        subtitle={subtitle ? subtitle : 'Hier können Sie für Teilnehmende einen Einladungstext verfassen.'}
        saveAsDefaultLabel={saveAsDefaultLabel}
        onChange={newFormData => {
          props.flowFunctions.setFlowData({ ...flowData, ...newFormData });
        }}
      />
    </FramedContent>
  );
};

export const ConnectedWorkgroupInvitationForm = (props: any) => {
  const flowAlias = useSelector(state => state.flow?.currentAlias);
  const invitationText = useSelector(state => state.detail?.item?.content?.invitation);
  const participants = useSelector(state => state.communication?.participants);

  const subtitle =
    flowAlias === 'createmeeting'
      ? 'Eingeladene Personen erhalten diese Nachricht per Mail zusammen mit den Informationen zum Meeting inkl. Link und ics-Datei.'
      : '';

  // Nur beim Einladen in ein bestehendes Objekt gibt es einen gespeicherten Einladungstext.
  // In den Anlege-Flows (groups-projects, createmeeting, chat) existiert das Objekt noch nicht;
  // dort wird der erste Einladungstext ohnehin serverseitig als allgemeiner Text übernommen.
  const isExistingCommunication = flowAlias === 'add-participants';

  // Gespeicherter Text hat Vorrang; nur wenn er fehlt (Bestandsgruppen), aus den offenen
  // Einladungen ableiten — dieselbe Regel wie im Sammelversand-Dialog.
  const prefillText = invitationText?.trim() || deriveInvitationText(participants);

  // Wichtig: FormInput übernimmt `value` nur beim Mount in seinen internen State und zieht
  // spätere Änderungen nicht nach. Ein Prefill, der erst per Effect über den Store läuft,
  // erreicht das sichtbare Feld deshalb nie. Der Text wird daher bereits beim ersten Render
  // in die ans Formular übergebenen Daten gemischt.
  const effectiveFlowData = useMemo(() => {
    if (!isExistingCommunication || !prefillText) return props.flowData;
    // Eine bereits getätigte Eingabe nicht überschreiben (z.B. beim Zurücknavigieren im Flow).
    if (props.flowData?.itemData?.invitation) return props.flowData;
    return { ...props.flowData, itemData: { ...props.flowData?.itemData, invitation: prefillText } };
  }, [isExistingCommunication, prefillText, props.flowData]);

  // Store einmalig nachziehen, damit der Saga-Worker beim Absenden denselben Text sieht,
  // auch wenn der Nutzer die Vorbelegung unverändert übernimmt.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !isExistingCommunication || !prefillText) return;
    if (props.flowData?.itemData?.invitation) return;
    prefilled.current = true;
    props.flowFunctions.setFlowData(effectiveFlowData);
  }, [isExistingCommunication, prefillText, effectiveFlowData, props]);

  return (
    <WorkgroupInvitationForm
      props={props}
      flowData={effectiveFlowData}
      subtitle={subtitle}
      saveAsDefaultLabel={isExistingCommunication ? 'Diesen Text als allgemeinen Einladungstext dieser Community speichern' : undefined}
    />
  );
};
